import React from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import moment from 'moment';
import { useAppSelector, useQueryParamValue } from 'src/utils/Hooks';
import { formatDisplayName } from 'src/utils/stringFormatting';
import BeaconMetricSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconMetricSplineChart';
import {
  keyMetricsReach,
  keyMetricsAdSpend,
  keyMetricsAdSales,
  keyMetricsIncrementality,
  AdvertisingMetric
} from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';

import { useEntityPhrasing } from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { AdMetricIndexNameByEntityType } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';

const getRequestBuilder = (metricId: string) => {
  return (requestBuilder: AdvancedSearchRequestBuilder) => {
    requestBuilder.removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId');
    if (metricId === AdvertisingMetric.TARGETS) {
      return requestBuilder;
    } else {
      return requestBuilder.addConditionTermFilter('isProjected', 'must', [0]);
    }
  };
};

const SelectedAdMetricChart = () => {
  const mainTimePeriod = useAppSelector((state) => state?.mainTimePeriod);
  const comparisonTimePeriodDisplay = useAppSelector((state) => state?.comparisonTimePeriod?.shortDisplayName);
  let entityPhrase = useEntityPhrasing();
  const entityType = useMainEntityType();
  const isYTD = mainTimePeriod?.id === 'ytd';
  const isPriorYear = comparisonTimePeriodDisplay === 'Prior Year';

  if (entityType === 'product') {
    entityPhrase = '';
  }

  const selectedMetricId = useQueryParamValue('selectedMetric', 'impressions');
  const selectedMetric = [
    ...keyMetricsReach,
    ...keyMetricsAdSpend,
    ...keyMetricsAdSales,
    ...keyMetricsIncrementality
  ].find((metric) => {
    return metric.id === selectedMetricId;
  });

  if (!selectedMetric) {
    return null;
  }

  const requestBuilder = getRequestBuilder(selectedMetricId);
  const adjustedEntityPhrase = entityType === 'adCampaign' ? 'this campaign' : entityPhrase;

  return (
    <BeaconMetricSplineChart
      byDayId
      indexName={
        selectedMetricId === AdvertisingMetric.TARGETS
          ? 'adCampaignAdGroupProductTargetDailyMetrics'
          : AdMetricIndexNameByEntityType[entityType]
      }
      beaconChartWithLegendOverride={(props) => {
        return {
          ...props,
          includeSummaryInfo: false,

          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1">
                The chart displays the {selectedMetric?.label?.toLowerCase()} for {adjustedEntityPhrase} during the{' '}
                {formatDisplayName(mainTimePeriod)} period compared to the {comparisonTimePeriodDisplay?.toLowerCase()}.
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
      requestBuilder={requestBuilder}
      fieldName={selectedMetricId}
      title={selectedMetric.label}
      customFilterComparisonForTotal={(row) => {
        const startDate = moment(mainTimePeriod?.startDayId, 'YYYYMMDD').subtract(1, 'year').valueOf();
        const endDate = moment(mainTimePeriod?.endDayId, 'YYYYMMDD').subtract(1, 'year').valueOf();
        if (isYTD && isPriorYear) {
          return row[0] >= startDate && row[0] <= endDate;
        }
        return true;
      }}
    />
  );
};

export default SelectedAdMetricChart;
